/**
 * Launcher V2 — pure reorder logic for the Qualify step's segmentation
 * waterfall (mounted on Glaze's `ReorderableList`).
 *
 * Two functions, one per side of the same drag:
 *   - `reorderIds` (A2 U5) — the client-side optimistic move, on the stable rung
 *     ids `ReorderableList` carries.
 *   - `applyRungReorder` (PR B U4) — the server-side remap of the persisted
 *     `PlaybooksPlaybookVersion.rungs` array into the requested order.
 *
 * The algorithm is factored out here, deliberately NOT importing `@dnd-kit`'s
 * own `arrayMove` — this file must stay importable from a DOM-less `bun:test`
 * run, and `@dnd-kit/sortable`'s barrel pulls in `@dnd-kit/core`, which (like
 * `@clay-run/glaze/dropdown`, see `config-page-logic.ts`'s note) is not safe
 * to import outside a real DOM. It is also imported by `v2/api.ts` on the
 * server, so it stays dependency-free (no zod, no Prisma): `applyRungReorder`
 * is generic over the rung shape rather than importing `PlaybookRung`.
 */

/**
 * Moves `activeId` to sit where `overId` currently sits, preserving every
 * other id's relative order. Returns the ids unchanged (new array, same
 * order) if `activeId`/`overId` are equal or either id isn't present — a
 * `ReorderableList` drag that starts and ends on the same item, or a stale
 * id from a race with a re-render, is a no-op rather than a corrupt reorder.
 */
export function reorderIds(ids: readonly string[], activeId: string, overId: string): string[] {
  const activeIndex = ids.indexOf(activeId);
  const overIndex = ids.indexOf(overId);
  if (activeId === overId || activeIndex === -1 || overIndex === -1) {
    return [...ids];
  }
  const next = ids.slice();
  next.splice(activeIndex, 1);
  next.splice(overIndex, 0, activeId);
  return next;
}

/** The minimum a rung has to expose for a reorder — `PlaybookRung` satisfies it structurally. */
interface ReorderableRung {
  id: string;
  position: number;
  isCatchAll: boolean;
}

/**
 * A fingerprint of a ladder's ORDER, used by `WaterfallCard` to decide when to
 * drop its optimistic local drag order and re-seed from the server's.
 *
 * The ids in array order, and nothing else. `position` is deliberately absent:
 * a successful reorder renumbers positions back to `0..n-1`, so the refetched
 * ladder carries the IDENTICAL position set in a different order and a
 * position-based fingerprint would not change — the stale local order would be
 * re-applied on top of already-reordered server data. Rung ids are unique
 * within a ladder (`PlaybookRungsSchema`) and never change once minted, so the
 * joined id list changes exactly when the ORDER changes, and never otherwise.
 */
export function rungOrderSignature(rungs: readonly { id: string }[]): string {
  return rungs.map((rung) => rung.id).join('|');
}

/**
 * A rejected reorder is a CALLER bug (a stale or fabricated rung-id list), not
 * a server fault, so it is returned rather than thrown: the endpoint turns
 * `ok: false` into a 400 with `reason` as the detail, and never reaches
 * `createVersion`.
 */
export type RungReorderResult<T> = { ok: true; rungs: T[] } | { ok: false; reason: string };

/**
 * Rebuild a playbook's ladder in the order given by `orderedRungIds` (the
 * stable `id`s of the non-catch-all rungs, in their new priority order).
 *
 * Ids, not positions: `position` is only a rung's ORDER, and this call is
 * precisely the write that rewrites it, so a position can never address "this
 * rung" across it. Positions are renumbered `0..n-1` to ascend with the new
 * array order and the catch-all is re-pinned last with the next position — the
 * invariants `PlaybookRungsSchema`'s `superRefine` enforces on write. Every
 * rung's `id` rides through untouched.
 *
 * `orderedRungIds` must be an exact permutation of the ladder's non-catch-all
 * rung ids. A short, long, duplicated or unknown-id list would silently drop or
 * clone a rung, so it is refused instead. The catch-all never appears in
 * `orderedRungIds` (it is pinned last and excluded from the drag list client
 * side); including it is rejected as an unknown id, i.e. a non-permutation.
 */
export function applyRungReorder<T extends ReorderableRung>(
  rungs: readonly T[],
  orderedRungIds: readonly string[],
): RungReorderResult<T> {
  const catchAll = rungs.find((rung) => rung.isCatchAll) ?? null;
  const reorderable = rungs.filter((rung) => !rung.isCatchAll);
  const byId = new Map(reorderable.map((rung) => [rung.id, rung]));

  const expected = reorderable.map((rung) => rung.id);
  const requested = [...orderedRungIds];
  const isPermutation =
    requested.length === expected.length &&
    new Set(requested).size === requested.length &&
    requested.every((id) => byId.has(id));
  if (!isPermutation) {
    return {
      ok: false,
      reason: `orderedRungIds must be a permutation of this playbook's non-catch-all rung ids [${expected.join(', ')}] — received [${requested.join(', ')}].`,
    };
  }

  const reordered = requested.map((id, index) => ({
    ...(byId.get(id) as T),
    position: index,
  }));
  return {
    ok: true,
    rungs: catchAll ? [...reordered, { ...catchAll, position: reordered.length }] : reordered,
  };
}
